import { generateURL } from 'utils/helpers';
import { addProductPageId } from 'utils/helpers/crm';
import { apiSlice } from '.';

apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    create: builder.mutation({
      query: (args) => {
        const body = args.map((arg) => addProductPageId(arg));

        return {
          url: generateURL('/reservation/create'),
          method: 'POST',
          body,
        };
      },
    }),
    createFerryReservation: builder.mutation({
      query: (args) => {
        const body = addProductPageId(args);

        return {
          url: generateURL('/reservation/scheduled_trip/create'),
          method: 'POST',
          body,
        };
      },
    }),

    /**
     * The parameter structures are different between Magnetic Island and Rottnest Island.
     * In Magnetic Island, the parameter is an array of objects, like the following:
     * ```
     * [{
     *   first_travel_date: '2022-01-01',
     *   quantity: 1,
     *   resource_id: 805,
     * }]
     * ```
     *
     * In Rottnest Island, when adding optional extras, the parameter is an object, like the following:
     * ```
     * {
     *   ferryGrouping: {},
     *   reservations: [{
     *     first_travel_date: '2022-01-01',
     *     quantity: 1,
     *     resource_id: 805,
     *  }]
     * }
     * ```
     */
    createTicket: builder.mutation({
      query: (args) => {
        let body;

        if (Array.isArray(args)) {
          body = args.map((arg) => addProductPageId(arg));
        } else {
          body = {
            ferryGrouping: args.ferryGrouping,
            reservations: args.reservations.map((arg) => addProductPageId(arg)),
          };
        }

        return {
          url: generateURL('/reservation/ticket/create'),
          method: 'POST',
          body,
        };
      },
    }),
    calendarAvailability: builder.query({
      query: (args) => {
        const params = {
          product_type_id: args.productTypeId,
          route_id: args.primaryRouteId,
          resource_ids: args.routeIds,
          date_from: args.fromDate,
          date_to: args.toDate,
          journey: {
            passenger_types: args.passengerTypes || { 1: 1 },
            vehicle_types: args.vehicleTypes || {},
            from_route_stop_id: args.fromRouteStopId,
            to_route_stop_id: args.toRouteStopId,
          },
        };

        return {
          url: generateURL(
            '/reservation/scheduled_trips/calendar_availability'
          ),
          method: 'POST',
          body: params,
        };
      },
      providesTags: ['reservation'],
      keepUnusedDataFor: 3600,
    }),
    findServicesFor: builder.query({
      query: (args) => ({
        url: generateURL('/reservation/generics/find'),
        method: 'POST',
        body: args,
      }),
      providesTags: ['reservation'],
      keepUnusedDataFor: 3600,
    }),
    serviceAvailability: builder.query({
      query: (args) => {
        const params = {
          add_return: args.addReturn || false,
          product_type_id: args.productTypeId,
          route_id: args.routeId,
          show: 'inventory_exists',
          from_route_stop_id: args.fromRouteStopId,
          to_route_stop_id: args.toRouteStopId,
          get_availability: args.getAvailability || false,
          forward: {
            first_travel_date: args.firstTravelDate,
            reservation_id: args.reservationId,
            passenger_types: args.passengerTypes || { 1: 1 },
            vehicle_types: args.vehicleTypes || [],
            from_route_stop_id: args.fromRouteStopId,
            to_route_stop_id: args.toRouteStopId,
          },
        };

        return {
          url: generateURL('/reservation/scheduled_trips/find'),
          method: 'POST',
          body: params,
        };
      },
      providesTags: ['/reservation/scheduled_trips/find'],
      keepUnusedDataFor: 3600,
    }),
    autoSelectScheduledTrips: builder.mutation({
      query: (args) => ({
        url: generateURL('/reservation/scheduled_trips/auto_select'),
        method: 'POST',
        body: args,
      }),
    }),
    delete: builder.mutation({
      query: ({ bookingId, reservationId }) => ({
        url: generateURL(`/booking/${bookingId}/reservation/${reservationId}`),
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useCreateMutation,
  useCreateFerryReservationMutation,
  useCreateTicketMutation,
  useCalendarAvailabilityQuery,
  useFindServicesForQuery,
  useServiceAvailabilityQuery,
  useLazyServiceAvailabilityQuery,
  useLazyCalendarAvailabilityQuery,
  useAutoSelectScheduledTripsMutation,
  useDeleteMutation,
} = apiSlice;
